import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { CheckCircle, Copy, QrCode, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentApproved, setPaymentApproved] = useState(false);

  const createPayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payments/create-pix?amount=19.90&description=Plano Premium MeuGestor - Mensal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayment(response.data);
      toast.success('Pagamento PIX gerado!');
      
      // Start checking status
      startStatusCheck(response.data.transaction_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const startStatusCheck = (transactionId) => {
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/payments/status/${transactionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'approved') {
          setPaymentApproved(true);
          clearInterval(interval);
          
          // Update user in localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.subscription_status = 'active';
          localStorage.setItem('user', JSON.stringify(user));
          
          toast.success('Pagamento aprovado! Plano ativado!');
          setTimeout(() => navigate('/dashboard'), 3000);
        }
      } catch (error) {
        console.error('Error checking status:', error);
      }
    }, 5000); // Check every 5 seconds

    // Stop after 10 minutes
    setTimeout(() => clearInterval(interval), 600000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado!');
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        <button
          onClick={() => navigate('/plans')}
          className="flex items-center text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </button>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Ativar Plano Premium
            </h1>
            <p className="text-slate-400">Pagamento via PIX - R$ 19,90/mês</p>
          </div>

          {!payment && !loading && (
            <div className="text-center">
              <div className="bg-violet-600/10 border border-violet-500/30 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold text-violet-400 mb-4">O que você vai receber:</h3>
                <ul className="text-left text-slate-300 space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Clientes ilimitados</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Múltiplas instâncias WhatsApp</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Automação completa de cobranças</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Templates ilimitados</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span>Suporte prioritário</span>
                  </li>
                </ul>
              </div>
              <Button
                onClick={createPayment}
                className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-lg"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Gerando PIX...
                  </>
                ) : (
                  'Gerar Pagamento PIX'
                )}
              </Button>
            </div>
          )}

          {payment && !paymentApproved && (
            <div className="space-y-6">
              {/* QR Code */}
              {payment.qr_code_base64 && (
                <div className="bg-white rounded-xl p-6 flex flex-col items-center">
                  <QrCode className="w-8 h-8 text-violet-600 mb-4" />
                  <p className="text-slate-700 font-medium mb-4">Escaneie o QR Code com seu app de pagamento</p>
                  <img
                    src={`data:image/png;base64,${payment.qr_code_base64}`}
                    alt="QR Code PIX"
                    className="w-64 h-64"
                  />
                </div>
              )}

              {/* Pix Code */}
              {payment.qr_code && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Ou use o código PIX copia e cola:
                  </label>
                  <div className="relative">
                    <textarea
                      readOnly
                      value={payment.qr_code}
                      className="w-full h-24 rounded-lg border border-zinc-700 bg-zinc-800 text-white px-4 py-3 text-sm font-mono resize-none"
                    />
                    <Button
                      onClick={() => copyToClipboard(payment.qr_code)}
                      className="absolute top-2 right-2 bg-violet-600 hover:bg-violet-700"
                      size="sm"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar
                    </Button>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                  <div>
                    <p className="text-amber-400 font-medium">Aguardando pagamento...</p>
                    <p className="text-xs text-amber-300 mt-1">
                      O status será atualizado automaticamente após o pagamento
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {paymentApproved && (
            <div className="text-center">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8">
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-emerald-400 mb-2">
                  Pagamento Aprovado!
                </h3>
                <p className="text-slate-300 mb-4">
                  Seu plano foi ativado com sucesso!
                </p>
                <p className="text-sm text-slate-400">
                  Redirecionando para o dashboard...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
